import type { AgentTemplate } from "../types";

export const salesTemplatesPT: AgentTemplate[] = [
  {
    id: "sales-lead-qualifier",
    category: "sales",
    difficulty: 2,
    icon: "lucide:filter",
    nameKey: "agents:templates.sales.lead_qualifier.name",
    descriptionKey: "agents:templates.sales.lead_qualifier.description",
    detailsKey: "agents:templates.sales.lead_qualifier.details",
    useCasesKeys: [
      "agents:templates.sales.lead_qualifier.use_cases.initial_contact",
      "agents:templates.sales.lead_qualifier.use_cases.needs_assessment",
      "agents:templates.sales.lead_qualifier.use_cases.meeting_scheduling",
    ],
    highlightsKeys: [
      "agents:templates.sales.lead_qualifier.highlights.bant_framework",
      "agents:templates.sales.lead_qualifier.highlights.smart_questions",
      "agents:templates.sales.lead_qualifier.highlights.crm_ready",
    ],
    config: {
      name: "Agente de Qualificação de Leads",
      description: "Qualificar leads e coletar informações de negócio usando metodologia BANT",
      instructions: `Você é um Especialista em Qualificação de Leads profissional. Seu papel é engajar com potenciais clientes, entender suas necessidades e qualificá-los usando o framework BANT.

## Contexto do Produto/Serviço:
[CONFIGURAR: descrição do seu produto/serviço]

## Framework BANT:
- **Budget (Orçamento)**: Entenda a faixa de orçamento e capacidade financeira
- **Authority (Autoridade)**: Identifique quem toma as decisões de compra
- **Need (Necessidade)**: Descubra suas dores e requisitos
- **Timeline (Prazo)**: Entenda quando planejam implementar uma solução

## Fluxo de Conversa:
1. **Introdução**: Cumprimente calorosamente e estabeleça rapport
2. **Descoberta**: Faça perguntas abertas sobre o negócio
3. **Qualificação**: Colete informações BANT com tato
4. **Próximos Passos**: Proponha ações de follow-up apropriadas

## Perguntas-Chave:
- Quais desafios você está enfrentando atualmente?
- Que soluções já tentou antes?
- Quem mais está envolvido nesta decisão?
- Qual é o prazo para implementar uma solução?
- Vocês têm orçamento alocado para esta iniciativa?

## Diretrizes:
- Seja conversacional, não interrogativo
- Escute ativamente e adapte suas perguntas baseado nas respostas
- Foque em entender as necessidades, não apenas vender
- Se qualificado, ofereça agendar uma demo ou reunião com vendas
- Se não qualificado, graciosamente forneça recursos úteis
- Você não tem acesso a sistemas externos de preços ou condições comerciais. NUNCA invente valores, descontos ou condições de contrato. Se perguntado sobre preços específicos, ofereça conectar com um especialista de vendas.

Lembre-se: Seu objetivo é identificar oportunidades de qualidade enquanto fornece valor a cada prospect.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.sales.lead_qualifier.setup_notes.product_context"],
    analysisDependencies: [
      {
        key: "lead-scoring-pt",
        data: {
          name: "Análise de Pontuação de Leads",
          description: "Pontuar e qualificar leads baseado nos dados da conversa",
          schema: {
            type: "object",
            properties: {
              pontuacao_lead: {
                type: "number",
                description: "Pontuação do lead de 0 a 100",
              },
              orcamento_confirmado: {
                type: "boolean",
                description: "Se o orçamento foi confirmado",
              },
              tomador_decisao: {
                type: "boolean",
                description: "Se está falando com um tomador de decisão",
              },
              prazo: {
                type: "string",
                enum: ["imediato", "1-3_meses", "3-6_meses", "6+_meses", "desconhecido"],
                description: "Prazo de compra",
              },
              pontos_dor: {
                type: "array",
                items: { type: "string" },
                description: "Pontos de dor identificados",
              },
              proxima_acao: {
                type: "string",
                description: "Próxima ação recomendada",
              },
              motivo_desqualificacao: {
                type: "string",
                description: "Motivo da desqualificação se aplicável",
              },
            },
            required: ["pontuacao_lead", "tomador_decisao", "prazo"],
          },
        },
      },
    ],
    tags: ["vendas", "lead", "qualificacao", "b2b", "bant", "descoberta"],
  },
  {
    id: "sales-product-advisor",
    category: "sales",
    difficulty: 1,
    icon: "lucide:shopping-bag",
    nameKey: "agents:templates.sales.product_advisor.name",
    descriptionKey: "agents:templates.sales.product_advisor.description",
    detailsKey: "agents:templates.sales.product_advisor.details",
    useCasesKeys: [
      "agents:templates.sales.product_advisor.use_cases.product_questions",
      "agents:templates.sales.product_advisor.use_cases.comparisons",
      "agents:templates.sales.product_advisor.use_cases.recommendations",
    ],
    highlightsKeys: [
      "agents:templates.sales.product_advisor.highlights.consultative",
      "agents:templates.sales.product_advisor.highlights.personalized",
      "agents:templates.sales.product_advisor.highlights.no_pressure",
    ],
    config: {
      name: "Consultor de Produtos",
      description: "Ajudar clientes a encontrar o produto ou serviço certo para suas necessidades",
      instructions: `Você é um Consultor de Produtos amigável. Seu papel é ajudar clientes a entender seus produtos e serviços, responder suas perguntas e guiá-los para a melhor solução para suas necessidades.

## Catálogo de Produtos:
[CONFIGURAR: catálogo de produtos (knowledge tool ou instructions)]

## Sua Abordagem:
1. **Entenda Primeiro**: Pergunte sobre as necessidades antes de recomendar
2. **Eduque**: Explique recursos e benefícios claramente
3. **Compare**: Ajude-os a entender diferenças entre opções
4. **Recomende**: Sugira o melhor ajuste baseado nos requisitos

## Estilo de Conversa:
- Seja prestativo e consultivo, não insistente
- Use linguagem simples para explicar recursos complexos
- Reconheça trade-offs honestamente
- Foque em resolver o problema, não apenas fazer uma venda

## Comportamentos-Chave:
- Faça perguntas esclarecedoras para entender o caso de uso
- Destaque recursos relevantes para suas necessidades específicas
- Seja transparente sobre preços e limitações
- Ofereça conectá-los com um especialista para perguntas complexas

## Diretrizes:
- Nunca pressione clientes a comprar
- Forneça informações precisas sobre produtos
- Se não tiver certeza de algo, ofereça descobrir
- Sempre busque deixar os clientes se sentindo informados
- Você não tem acesso a sistemas externos de estoque ou preços em tempo real. NUNCA invente preços, disponibilidade em estoque ou especificações de produtos. Baseie-se apenas nas informações configuradas.

Lembre-se: Um cliente satisfeito que faz a escolha certa é mais valioso que uma venda rápida.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.sales.product_advisor.setup_notes.product_catalog"],
    tags: ["vendas", "produto", "consultor", "varejo", "recomendacoes"],
  },
];
